/* eslint-disable max-lines */
import { useState } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, ModalFooter } from 'reactstrap';
import SignatureCanvas from 'react-signature-canvas';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import HireDocuments from '../../../../services/HireDocuments';
import { toast } from 'react-toastify';

export interface IS54FormData {
  employerName: string;
  dbaName: string;
  fein: string;
  physicalAddress: string;
  mailingAddress: string;
  employerPhone: string;
  noticeGivenAtHiring: boolean;
  noticeGivenBeforeChange: boolean;
  employeeRateOfPay: string;
  allowanceNone: boolean;
  allowanceTips: boolean;
  allowanceTipsAmount: string;
  allowanceMeals: boolean;
  allowanceMealsAmount: string;
  allowanceLodging: boolean;
  allowanceLodgingAmount: string;
  allowanceOther: boolean;
  allowanceOtherDescription: string;
  regularPayday: string;
  payFrequencySelection: 'weekly' | 'biWeekly' | 'other' | '';
  overtimePayRate: string;
  ackLanguageSelection: 'english' | 'otherLanguage' | '';
  ackPrimaryLanguage: string;
  printEmployeeName: string;
  employeeSignature: string;
  date: string;
  preparerNameAndTitle: string;
}

const IS54Form = ({ onChange, currentUser }: { onChange: (data: IS54FormData) => void; currentUser: any }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<IS54FormData>({
    employerName: currentUser?.school?.name || '',
    dbaName: currentUser?.school?.name || '',
    fein: currentUser?.school?.federalTaxId || '',
    physicalAddress: currentUser?.school?.address || '',
    mailingAddress: currentUser?.school?.address || '',
    employerPhone: currentUser?.school?.phone || '',
    noticeGivenAtHiring: false,
    noticeGivenBeforeChange: false,
    employeeRateOfPay: Number(currentUser?.payRates?.length ? currentUser?.payRates[0]?.payRate : 0).toFixed(2),
    allowanceNone: true,
    allowanceTips: false,
    allowanceTipsAmount: '',
    allowanceMeals: false,
    allowanceMealsAmount: '',
    allowanceLodging: false,
    allowanceLodgingAmount: '',
    allowanceOther: false,
    allowanceOtherDescription: '',
    regularPayday: '',
    payFrequencySelection: '',
    overtimePayRate: '',
    ackLanguageSelection: '',
    ackPrimaryLanguage: '',
    printEmployeeName: '',
    employeeSignature: '',
    date: '',
    preparerNameAndTitle: '',
  });

  const [sigPad, setSigPad] = useState<any>();
  const [signature, setSignature] = useState<any>('');

  const getSignature = () => {
    if (sigPad) {
      const img = sigPad.getTrimmedCanvas()?.toDataURL('image/png');
      if (img) {
        setSignature(img);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    const body = {
      type: 'IS54',
      userId: currentUser?.userId || '',
      userName: currentUser?.userName || '',
      userToken: '',
      pdf: '',
      pdfUrl: 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/is-54-form.pdf',
      pdfSigned: signature,
      signature: signature,
      options: { ...formData, signatureDate: moment().format('YYYY-MM-DD') },
    };

    try {
      await HireDocuments('').create(body);
      toast.success(`Saved successfully!`);
      onChange(formData);
    } catch (e: any) {
      console.log(e);
      const message = String(e?.response?.data?.message || 'Create error!');
      toast.error(message);
    }

    setLoading(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (name === 'ackLanguageSelection') {
      setFormData((prevData) => ({
        ...prevData,
        ackLanguageSelection: value as 'english' | 'otherLanguage' | '',
        ackPrimaryLanguage: value === 'otherLanguage' ? prevData.ackPrimaryLanguage : '',
      }));
      return;
    }
    if (name === 'payFrequencySelection') {
      setFormData((prevData) => ({
        ...prevData,
        payFrequencySelection: value as 'weekly' | 'biWeekly' | 'other' | '',
      }));
      return;
    }
    if (name === 'employeeRateOfPay') {
      setFormData((prevData) => ({
        ...prevData,
        overtimePayRate: Number(Number(value) * 1.5).toFixed(2),
      }));
    }
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <Container>
      <Form>
        <h5 className="mt-4">1. Employer Information</h5>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="employerName">Name:</Label>
              <Input
                type="text"
                name="employerName"
                id="employerName"
                value={formData.employerName}
                onChange={handleInputChange}
                disabled
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="dbaName">Doing Business As (DBA) Name(s):</Label>
              <Input
                type="text"
                name="dbaName"
                id="dbaName"
                value={formData.dbaName}
                onChange={handleInputChange}
                disabled
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="fein">FEIN (optional):</Label>
              <Input type="text" name="fein" id="fein" value={formData.fein} onChange={handleInputChange} disabled />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="physicalAddress">Physical Address:</Label>
              <Input
                type="text"
                name="physicalAddress"
                id="physicalAddress"
                value={formData.physicalAddress}
                onChange={handleInputChange}
                disabled
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="mailingAddress">Mailing Address:</Label>
              <Input
                type="text"
                name="mailingAddress"
                id="mailingAddress"
                value={formData.mailingAddress}
                onChange={handleInputChange}
                disabled
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="employerPhone">Phone:</Label>
              <Input
                type="tel"
                name="employerPhone"
                id="employerPhone"
                value={formData.employerPhone}
                onChange={handleInputChange}
                disabled
              />
            </FormGroup>
          </Col>
        </Row>

        <h5 className="mt-4">2. Notice given:</h5>
        <FormGroup check>
          <Input
            type="checkbox"
            name="noticeGivenAtHiring"
            id="noticeGivenAtHiring"
            checked={formData.noticeGivenAtHiring}
            onChange={handleInputChange}
          />
          <Label check for="noticeGivenAtHiring">
            At hiring
          </Label>
        </FormGroup>
        <FormGroup check className="mb-3">
          <Input
            type="checkbox"
            name="noticeGivenBeforeChange"
            id="noticeGivenBeforeChange"
            checked={formData.noticeGivenBeforeChange}
            onChange={handleInputChange}
          />
          <Label check for="noticeGivenBeforeChange">
            Before a change in pay rate(s), allowances claimed or payday
          </Label>
        </FormGroup>

        <h5 className="mt-4">3. Employee's rate of pay:</h5>
        <FormGroup>
          <Row className="align-items-center">
            <Col xs="auto" className="pe-1">
              $
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Input
                name="employeeRateOfPay"
                id="employeeRateOfPay"
                type="text"
                thousandsGroupStyle="thousand"
                prefix="$"
                decimalSeparator="."
                displayType="input"
                thousandSeparator={true}
                allowNegative={false}
                decimalScale={2}
                tag={NumberFormat}
                value={formData.employeeRateOfPay}
                onValueChange={handleInputChange}
                disabled
              />
            </Col>
            <Col xs="auto" className="ps-1">
              per hour
            </Col>
          </Row>
        </FormGroup>

        <h5 className="mt-4">4. Allowances taken:</h5>
        <FormGroup check>
          <Input
            type="checkbox"
            name="allowanceNone"
            id="allowanceNone"
            checked={formData.allowanceNone}
            onChange={handleInputChange}
          />
          <Label check for="allowanceNone">
            None
          </Label>
        </FormGroup>

        <Row className="align-items-center mt-2">
          <Col md={3} xs={5}>
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                name="allowanceTips"
                id="allowanceTips"
                checked={formData.allowanceTips}
                onChange={handleInputChange}
              />
              <Label check for="allowanceTips">
                Tips
              </Label>
            </FormGroup>
          </Col>
          <Col md={2} xs={3}>
            <Input
              type="text"
              name="allowanceTipsAmount"
              id="allowanceTipsAmount"
              value={formData.allowanceTipsAmount}
              onChange={handleInputChange}
              disabled={!formData.allowanceTips}
              bsSize="sm"
            />
          </Col>
          <Col md={7} xs={4} className="ps-1">
            per hour
          </Col>
        </Row>

        <Row className="align-items-center mt-2">
          <Col md={3} xs={5}>
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                name="allowanceMeals"
                id="allowanceMeals"
                checked={formData.allowanceMeals}
                onChange={handleInputChange}
              />
              <Label check for="allowanceMeals">
                Meals
              </Label>
            </FormGroup>
          </Col>
          <Col md={2} xs={3}>
            <Input
              type="text"
              name="allowanceMealsAmount"
              id="allowanceMealsAmount"
              value={formData.allowanceMealsAmount}
              onChange={handleInputChange}
              disabled={!formData.allowanceMeals}
              bsSize="sm"
            />
          </Col>
          <Col md={7} xs={4} className="ps-1">
            per meal
          </Col>
        </Row>

        <Row className="align-items-center mt-2">
          <Col md={3} xs={5}>
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                name="allowanceLodging"
                id="allowanceLodging"
                checked={formData.allowanceLodging}
                onChange={handleInputChange}
              />
              <Label check for="allowanceLodging">
                Lodging
              </Label>
            </FormGroup>
          </Col>
          <Col md={3} xs={4}>
            <Input
              type="text"
              name="allowanceLodgingAmount"
              id="allowanceLodgingAmount"
              value={formData.allowanceLodgingAmount}
              onChange={handleInputChange}
              disabled={!formData.allowanceLodging}
              bsSize="sm"
            />
          </Col>
        </Row>

        <Row className="align-items-center mt-2 mb-3">
          <Col md={3} xs={5}>
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                name="allowanceOther"
                id="allowanceOther"
                checked={formData.allowanceOther}
                onChange={handleInputChange}
              />
              <Label check for="allowanceOther">
                Other
              </Label>
            </FormGroup>
          </Col>
          <Col md={4} xs={7}>
            <Input
              type="text"
              name="allowanceOtherDescription"
              id="allowanceOtherDescription"
              value={formData.allowanceOtherDescription}
              onChange={handleInputChange}
              disabled={!formData.allowanceOther}
              bsSize="sm"
            />
          </Col>
        </Row>

        <h5 className="mt-4">5. Regular payday:</h5>
        <FormGroup>
          <Col sm={8} md={6} lg={4}>
            <Input
              type="text"
              name="regularPayday"
              id="regularPayday"
              value={formData.regularPayday}
              onChange={handleInputChange}
            />
          </Col>
        </FormGroup>

        <h5 className="mt-4">6. Pay is:</h5>
        <FormGroup check>
          <Input
            type="radio"
            name="payFrequencySelection"
            id="payIsWeeklyRadio"
            value="weekly"
            checked={formData.payFrequencySelection === 'weekly'}
            onChange={handleInputChange}
          />
          <Label check for="payIsWeeklyRadio">
            Weekly
          </Label>
        </FormGroup>
        <FormGroup check>
          <Input
            type="radio"
            name="payFrequencySelection"
            id="payIsBiWeeklyRadio"
            value="biWeekly"
            checked={formData.payFrequencySelection === 'biWeekly'}
            onChange={handleInputChange}
          />
          <Label check for="payIsBiWeeklyRadio">
            Bi-weekly
          </Label>
        </FormGroup>
        <FormGroup check className="mb-3">
          <Input
            type="radio"
            name="payFrequencySelection"
            id="payIsOtherRadio"
            value="other"
            checked={formData.payFrequencySelection === 'other'}
            onChange={handleInputChange}
          />
          <Label check for="payIsOtherRadio">
            Other
          </Label>
        </FormGroup>

        <h5 className="mt-4">7. Overtime Pay Rate:</h5>
        <FormGroup>
          <Row className="align-items-center">
            <Col xs="auto" className="pe-1">
              $
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Input
                name="overtimePayRate"
                id="overtimePayRate"
                type="text"
                thousandsGroupStyle="thousand"
                prefix="$"
                decimalSeparator="."
                displayType="input"
                thousandSeparator={true}
                allowNegative={false}
                decimalScale={2}
                tag={NumberFormat}
                value={Number(Number(formData.employeeRateOfPay) * 1.5).toFixed(2)}
                onValueChange={handleInputChange}
                disabled
              />
            </Col>
            <Col xs="auto" className="ps-1">
              per hour
            </Col>
          </Row>
          <small className="form-text text-muted">
            (This must be at least 1½ times the worker's regular rate with few exceptions.)
          </small>
        </FormGroup>

        <hr className="my-4" />

        <h5 className="mt-4">8. Employee Acknowledgement:</h5>
        <p>
          On this day I have been notified of my pay rate, overtime rate (if eligible), allowances, and designated pay
          day on the date given below. I told my employer what my primary language is.
        </p>
        <p className="fw-bold">Check one:</p>
        <FormGroup check>
          <Input
            type="radio"
            name="ackLanguageSelection"
            id="ackEnglishPrimaryRadio"
            value="english"
            checked={formData.ackLanguageSelection === 'english'}
            onChange={handleInputChange}
          />
          <Label check for="ackEnglishPrimaryRadio">
            I have been given this pay notice in English because it is my primary language.
          </Label>
        </FormGroup>

        <FormGroup check className="mt-2">
          <Input
            type="radio"
            name="ackLanguageSelection"
            id="ackOtherLanguageRadio"
            value="otherLanguage"
            checked={formData.ackLanguageSelection === 'otherLanguage'}
            onChange={handleInputChange}
          />
          <Label check for="ackOtherLanguageRadio">
            My primary language is
          </Label>
        </FormGroup>

        <div style={{ paddingLeft: '1.8rem' }} className="mt-1">
          <Row className="align-items-center">
            <Col xs={7} sm={5} md={4} lg={3}>
              <Input
                type="text"
                name="ackPrimaryLanguage"
                id="ackPrimaryLanguageInput"
                value={formData.ackPrimaryLanguage}
                onChange={handleInputChange}
                disabled={formData.ackLanguageSelection !== 'otherLanguage'}
                bsSize="sm"
                placeholder="Specify language"
              />
            </Col>
            <Col xs="auto" className="ps-0">
              .
            </Col>
          </Row>
          {formData.ackLanguageSelection === 'otherLanguage' && (
            <p className="mb-0 text-muted mt-1" style={{ fontSize: '0.9em' }}>
              I have been given this pay notice in English only, because the Department of Labor does not yet offer a
              pay notice form in my primary language.
            </p>
          )}
        </div>

        <FormGroup className="mt-3">
          <Label for="printEmployeeName">Print Employee Name</Label>
          <Input
            type="text"
            name="printEmployeeName"
            id="printEmployeeName"
            value={formData.printEmployeeName}
            onChange={handleInputChange}
          />
        </FormGroup>
        <FormGroup>
          <Row>
            <Col md="12">
              <Label for="employeeSignature">Employee Signature</Label>
            </Col>
            <Col md="12">
              <SignatureCanvas
                penColor="blue"
                canvasProps={{ width: 500, height: 200, className: 'sig-canvas' }}
                ref={(ref) => setSigPad(ref)}
                onEnd={getSignature}
              />
            </Col>
            <Col md="12">
              <Button
                onClick={() => {
                  sigPad?.clear();
                }}
                color="warning"
                size="sm"
                className="mt-1 mb-2"
              >
                Clear
              </Button>
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Label for="date">Date</Label>
          <Col sm={6} md={4} lg={3}>
            <Input type="date" name="date" id="date" value={formData.date} onChange={handleInputChange} />
          </Col>
        </FormGroup>
        <FormGroup>
          <Label for="preparerNameAndTitle">Preparer's Name and Title</Label>
          <Input
            type="text"
            name="preparerNameAndTitle"
            id="preparerNameAndTitle"
            value={formData.preparerNameAndTitle}
            onChange={handleInputChange}
          />
        </FormGroup>

        <div className="mt-4 mb-4 p-3 border rounded">
          <p className="fw-bold">
            The employee must receive a signed copy of this form. The employer must keep the original for 6 years.
          </p>
          <p className="fw-bold mb-0">
            Please note:{' '}
            <span className="fw-normal">
              It is unlawful for an employee to be paid less than an employee of the opposite sex for equal work.
              Employers also may not prohibit employees from discussing wages with their co-workers.
            </span>
          </p>
        </div>
        <ModalFooter>
          <Button color="secondary" onClick={() => onChange(formData)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </ModalFooter>
      </Form>
    </Container>
  );
};

export default IS54Form;
